/**
 * 개별 접근(멤버) 페이지 읽기 데이터 캐시.
 * - 재방문·새로고침 시 sessionStorage + 메모리로 Firestore 읽기 절감
 * - 수정·추가·삭제 시 도메인별 무효화 후 force 로드
 */

export type MemberPageCacheScope = {
  userId: string;
  classId: string;
  memberGroup: string;
  studentId?: string;
  classCode?: string;
};

export type MemberPageCacheDomain =
  | 'classMeta'
  | 'defaultSet'
  | 'studentProfile'
  | 'pointsTable'
  | 'assignmentHistory'
  | 'positiveHistory'
  | 'pendingAssignments'
  | 'routineToday'
  | 'generalNotices'
  | 'individualNotices'
  | 'itemCards';

const STORAGE_PREFIX = 'memberPageCache_v1:';
const memory = new Map<string, { data: unknown; ts: number }>();

function normalizeGroup(memberGroup?: string): string {
  return (memberGroup || 'A').toString().trim().toUpperCase();
}

function scopePrefix(scope: MemberPageCacheScope): string {
  const g = normalizeGroup(scope.memberGroup);
  const sid = scope.studentId?.trim() || '_class';
  return `${scope.userId}:${scope.classId}:${g}:${sid}`;
}

function cacheKey(scope: MemberPageCacheScope, domain: MemberPageCacheDomain): string {
  return `${STORAGE_PREFIX}${scopePrefix(scope)}:${domain}`;
}

function readEntry<T>(key: string): T | null {
  const mem = memory.get(key);
  if (mem) return mem.data as T;
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: T; ts: number };
    if (!parsed || parsed.data === undefined) return null;
    memory.set(key, parsed);
    return parsed.data;
  } catch {
    return null;
  }
}

function writeEntry<T>(key: string, data: T): void {
  const payload = { data, ts: Date.now() };
  memory.set(key, payload);
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
    }
  } catch {
    // quota 등 — 메모리만 유지
  }
}

function deleteKeysMatching(matcher: (key: string) => boolean): void {
  Array.from(memory.keys()).forEach((key) => {
    if (matcher(key)) memory.delete(key);
  });
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && matcher(key)) toRemove.push(key);
    }
    toRemove.forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export const studentMemberPageCache = {
  get<T>(scope: MemberPageCacheScope, domain: MemberPageCacheDomain): T | null {
    return readEntry<T>(cacheKey(scope, domain));
  },

  set<T>(scope: MemberPageCacheScope, domain: MemberPageCacheDomain, data: T): void {
    writeEntry(cacheKey(scope, domain), data);
  },

  has(scope: MemberPageCacheScope, domain: MemberPageCacheDomain): boolean {
    return this.get(scope, domain) !== null;
  },

  invalidate(scope: MemberPageCacheScope, domains: MemberPageCacheDomain[]): void {
    const prefix = scopePrefix(scope);
    const keyPrefix = `${STORAGE_PREFIX}${prefix}:`;
    const domainSet = new Set(domains);
    deleteKeysMatching((key) => {
      if (!key.startsWith(keyPrefix)) return false;
      const domain = key.slice(keyPrefix.length) as MemberPageCacheDomain;
      return domainSet.has(domain);
    });
  },

  /** 해당 멤버의 개별 접근 페이지 캐시 전체 삭제 */
  invalidateAllForStudent(scope: MemberPageCacheScope): void {
    const keyPrefix = `${STORAGE_PREFIX}${scopePrefix(scope)}:`;
    deleteKeysMatching((key) => key.startsWith(keyPrefix));
  },

  /** 클래스·그룹 공통 메타(멤버 무관) */
  invalidateClassMeta(scope: MemberPageCacheScope): void {
    const classScope: MemberPageCacheScope = {
      userId: scope.userId,
      classId: scope.classId,
      memberGroup: scope.memberGroup,
      studentId: '_class',
      classCode: scope.classCode
    };
    this.invalidate(classScope, ['classMeta', 'defaultSet', 'pointsTable']);
  },

  async getOrFetch<T>(
    scope: MemberPageCacheScope,
    domain: MemberPageCacheDomain,
    fetcher: () => Promise<T>,
    options?: { force?: boolean }
  ): Promise<T> {
    if (!options?.force) {
      const cached = this.get<T>(scope, domain);
      if (cached !== null) return cached;
    }
    const data = await fetcher();
    this.set(scope, domain, data);
    return data;
  }
};

export function invalidateMemberPageAfterProfileChange(scope: MemberPageCacheScope): void {
  studentMemberPageCache.invalidate(scope, ['studentProfile', 'itemCards']);
}

export function invalidateMemberPageAfterPointsChange(scope: MemberPageCacheScope): void {
  studentMemberPageCache.invalidate(scope, [
    'studentProfile',
    'pointsTable',
    'assignmentHistory',
    'positiveHistory'
  ]);
}

export function invalidateMemberPageAfterAssignmentChange(scope: MemberPageCacheScope): void {
  studentMemberPageCache.invalidate(scope, [
    'pendingAssignments',
    'assignmentHistory',
    'studentProfile',
    'pointsTable',
    'itemCards'
  ]);
}

export function invalidateMemberPageAfterRoutineChange(scope: MemberPageCacheScope): void {
  studentMemberPageCache.invalidate(scope, ['routineToday', 'studentProfile', 'pointsTable', 'assignmentHistory']);
}

export function invalidateMemberPageAfterStoreChange(scope: MemberPageCacheScope): void {
  studentMemberPageCache.invalidate(scope, ['studentProfile', 'itemCards']);
}

export function invalidateMemberPageAfterNoticeChange(
  scope: MemberPageCacheScope,
  kind: 'general' | 'individual' | 'both' = 'both'
): void {
  const domains: MemberPageCacheDomain[] = [];
  if (kind === 'general' || kind === 'both') domains.push('generalNotices');
  if (kind === 'individual' || kind === 'both') domains.push('individualNotices');
  studentMemberPageCache.invalidate(scope, domains);
}
